import { z } from 'zod';

// ─── Module / Agent / Tool ────────────────────────────────────────────────

export const toolSchema = z.object({
	name: z.string(),
	description: z.string(),
	callCount24h: z.number().int().nonnegative(),
});

export const agentFlowNodeSchema = z.object({
	name: z.string(),
	kind: z.enum(['pre', 'llm', 'tools', 'post', 'end']),
});

export const agentSchema = z.object({
	name: z.string(),
	model: z.string(),
	pattern: z.string(),
	description: z.string(),
	path: z.string(),
	tools: z.array(toolSchema),
	flow: z.array(agentFlowNodeSchema),
});

export const dependencyStatusSchema = z.enum(['up', 'degraded', 'down', 'disabled']);
export type DependencyStatus = z.infer<typeof dependencyStatusSchema>;

export const dependencyKindSchema = z.enum(['redis', 'postgres', 'qdrant', 'intercom', 'llm', 'incidents_db']);
export type DependencyKind = z.infer<typeof dependencyKindSchema>;

export const moduleDependencySchema = z.object({
	name: z.string(),
	kind: dependencyKindSchema,
	required: z.boolean(),
	status: dependencyStatusSchema,
	latencyMs: z.number().int().nullable().optional(),
	message: z.string().nullable().optional(),
	checkedAt: z.string(),
});

export const moduleHealthSchema = z.object({
	status: dependencyStatusSchema,
	dependencies: z.array(moduleDependencySchema),
	checkedAt: z.string(),
});

export const moduleSchema = z.object({
	name: z.string(),
	description: z.string(),
	path: z.string(),
	status: z.enum(['live', 'shadow', 'disabled']),
	agents: z.array(agentSchema),
	costs: z.object({
		last24h: z.number().nonnegative(),
		last7d: z.number().nonnegative(),
		currentMonth: z.number().nonnegative(),
		monthProjection: z.number().nonnegative(),
		avgPerTurn: z.number().nonnegative(),
	}),
	turns: z.object({
		last24h: z.number().int().nonnegative(),
		errors24h: z.number().int().nonnegative(),
		activeThreads: z.number().int().nonnegative(),
		toolCalls24h: z.number().int().nonnegative(),
	}),
	health: moduleHealthSchema.nullable().optional(),
});

export type Tool = z.infer<typeof toolSchema>;
export type Agent = z.infer<typeof agentSchema>;
export type Module = z.infer<typeof moduleSchema>;
export type AgentFlowNode = z.infer<typeof agentFlowNodeSchema>;
export type ModuleDependency = z.infer<typeof moduleDependencySchema>;
export type ModuleHealth = z.infer<typeof moduleHealthSchema>;

// ─── Conversation / Message ───────────────────────────────────────────────

export const messageRoleSchema = z.enum(['human', 'ai', 'tool', 'system']);
export type MessageRole = z.infer<typeof messageRoleSchema>;

export const conversationStatusSchema = z.enum(['active', 'handoff', 'closed', 'tool_error']);
export type ConversationStatus = z.infer<typeof conversationStatusSchema>;

export const toolCallSchema = z.object({
	id: z.string(),
	name: z.string(),
	args: z.record(z.string(), z.unknown()),
	output: z.unknown().optional(),
	durationMs: z.number().int().nonnegative(),
	costUsd: z.number().nonnegative(),
});

// Customer attachment (image or PDF). `url` is directly renderable: an S3
// presigned URL for fresh rows or a `data:` URI for legacy rows that still
// carry the base64 inline in the audit log.
export const messageAttachmentSchema = z.object({
	name: z.string(),
	contentType: z.string(),
	url: z.string(),
});

export const messageSchema = z.object({
	id: z.string(),
	role: messageRoleSchema,
	content: z.string(),
	createdAt: z.string(),
	authorName: z.string().nullish(),
	authorInitials: z.string().nullish(),
	model: z.string().nullish(),
	costUsd: z.number().nonnegative().nullish(),
	tokensIn: z.number().int().nonnegative().nullish(),
	tokensOut: z.number().int().nonnegative().nullish(),
	toolCalls: z.array(toolCallSchema).nullish(),
	attachments: z.array(messageAttachmentSchema).nullish(),
});

export const evaluationSummarySchema = z.object({
	status: z.enum(['pending', 'completed', 'not_applicable']),
	totalJudges: z.number().int().nonnegative(),
	passedJudges: z.number().int().nonnegative(),
	hasFailures: z.boolean(),
});
export type EvaluationSummary = z.infer<typeof evaluationSummarySchema>;

export const conversationListItemSchema = z.object({
	threadId: z.string(),
	userName: z.string(),
	userInitials: z.string(),
	companyName: z.string(),
	country: z.string(),
	channel: z.string(),
	messageCount: z.number().int().nonnegative(),
	costUsd: z.number().nonnegative(),
	lastActivity: z.string(),
	startedAt: z.string(),
	status: conversationStatusSchema,
	evaluationSummary: evaluationSummarySchema.nullish(),
});

export const conversationDetailSchema = conversationListItemSchema.extend({
	userId: z.string(),
	companyId: z.string(),
	moduleName: z.string(),
	agentName: z.string(),
	tokensIn: z.number().int().nonnegative(),
	tokensOut: z.number().int().nonnegative(),
	toolCallCount: z.number().int().nonnegative(),
	messages: z.array(messageSchema),
	rawState: z.record(z.string(), z.unknown()),
});

export type ToolCall = z.infer<typeof toolCallSchema>;
export type MessageAttachment = z.infer<typeof messageAttachmentSchema>;
export type Message = z.infer<typeof messageSchema>;
export type ConversationListItem = z.infer<typeof conversationListItemSchema>;
export type ConversationDetail = z.infer<typeof conversationDetailSchema>;

// ─── Metrics ──────────────────────────────────────────────────────────────

export const metricRangeSchema = z.enum(['24h', '7d', '30d']);
export type MetricRange = z.infer<typeof metricRangeSchema>;

export const metricDeltaDirectionSchema = z.enum(['up', 'down', 'flat']);
export type MetricDeltaDirection = z.infer<typeof metricDeltaDirectionSchema>;

export const metricStatSchema = z.object({
	value: z.number(),
	formatted: z.string(),
	deltaPct: z.number().nullable(),
	deltaDirection: metricDeltaDirectionSchema,
	deltaLabel: z.string(),
	spark: z.array(z.number()),
});

export const metricsSchema = z.object({
	range: metricRangeSchema,
	turns: metricStatSchema,
	errors: metricStatSchema,
	activeThreads: metricStatSchema,
	toolCalls: metricStatSchema,
	cost: metricStatSchema.extend({
		monthToDateFormatted: z.string(),
	}),
	toolUsage: z.array(
		z.object({
			name: z.string(),
			count: z.number().int().nonnegative(),
			percent: z.number().min(0).max(100),
		}),
	),
	turnsByDay: z.array(
		z.object({
			dayLabel: z.string(),
			count: z.number().int().nonnegative(),
			isToday: z.boolean(),
		}),
	),
});

export type MetricStat = z.infer<typeof metricStatSchema>;
export type Metrics = z.infer<typeof metricsSchema>;
